import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ChevronRightIcon,
  EyeIcon,
  Loader2Icon,
  PencilIcon,
  SaveIcon,
  XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import type { DocumentData, Language } from '@worldathletics/types';
import {
  PageContentLayout,
  PageContentLayoutHeader,
  PageContentLayoutMain,
  PageContentLayoutSubheader,
} from '@/components/layout';
import { Button, TitleInputField } from '@/components/atoms';
import {
  DocumentAddCtaButton,
  DocumentEditorNode,
} from '@/components/document';
import { ModalComponent } from '@/components/modal';
import { AddContentButton, ReferencerModal } from '@/components/modals';
import { CustomTag, CustomTitle } from '@/components/typography';
import { MODAL_REFERENCER_STYLES, MODAL_ADD_BLOCK_STYLES } from '@/components/modal/constants';
import type { RootState } from '@/context/store/store';
import {
  openSpecialUrlModal,
  setEditingAction,
  setNewBlockModal,
} from '@/context/store/slices/siteSlice';
import {
  addNode,
  setDocumentData,
  setIsDirty,
} from '@/context/store/slices/documentDataSlice';
import { denormalizeDocumentData } from '@/context/store/helpers';
import { useCreateDocumentQuery } from '@/query/book/useCreateDocumentQuery';
import { useSaveDraftMutation } from '@/query/node/useSaveDraftMutation';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { requirePermission } from '@/lib/route-guards';
import { DEFAULT_LANGUAGE } from '@/lib/site-config';
import { Permission } from '@worldathletics/utilities';
import '@/styles/route-documentid.scss';

export const Route = createFileRoute('/_auth/create')({
  beforeLoad: () => {
    requirePermission(Permission.DocumentsCreate);
  },
  component: DocumentCreatePage,
});

function DocumentCreatePage() {
  const isEditingAction = useSelector(
    (state: RootState) => state.site.editingAction,
  );
  const addNewBlockModal = useSelector(
    (state: RootState) => state.site.addNewBlockModal,
  );
  const specialUrlModal = useSelector(
    (state: RootState) => state.site.specialUrlModal,
  );
  const rootIds = useSelector((state: RootState) => state.documentData.rootIds);
  const isDirty = useSelector((state: RootState) => state.documentData.isDirty);
  const documentData = useSelector((state: RootState) => state.documentData);

  const [bookName, setBookName] = useState('');
  // `language` is fixed to the default for now; multilingual support is added
  // post-create via the AddNewLanguage modal on the document detail page.
  const [language] = useState<Language>(DEFAULT_LANGUAGE);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const createBook = useCreateDocumentQuery();
  const saveDraft = useSaveDraftMutation();

  const handlePreview = () => {
    dispatch(setEditingAction(!isEditingAction));
  };

  const handleSpecialUrlModal = (b: boolean) => {
    dispatch(openSpecialUrlModal(b));
  };

  const saveCreateDraft = useCallback(
    async (options?: { navigateToBook?: boolean }): Promise<boolean> => {
      const trimmed = bookName.trim();
      if (!trimmed) {
        toast.error('Enter a book title before saving');
        return false;
      }
      if (createBook.isPending || saveDraft.isPending) return false;
      try {
        const created = await createBook.mutateAsync({ name: trimmed });
        const blocks = denormalizeDocumentData(documentData);
        await saveDraft.mutateAsync({
          bookId: created.id,
          body: {
            language,
            data: blocks as unknown as never[],
          },
        });
        dispatch(setIsDirty(false));
        toast.success('Draft created');
        if (options?.navigateToBook) {
          navigate({ to: `/document/${created.id}`, search: {} });
        }
        return true;
      } catch {
        return false;
      }
    },
    [bookName, createBook, documentData, dispatch, language, navigate, saveDraft],
  );

  const unsavedChangesModal = useUnsavedChangesGuard({
    isDirty,
    onSave: () => saveCreateDraft(),
  });

  const handleSaveDraft = async () => {
    await saveCreateDraft({ navigateToBook: true });
  };

  const handleAddPart = () => {
    const index = rootIds.length + 1;
    const node: DocumentData = {
      id: crypto.randomUUID(),
      type: 'container',
      style: 'part',
      order: index,
      title: 'Part',
      blocks: [],
    };
    dispatch(addNode({ node, parentId: null, index }));
  };

  const handleSubmit = () => {
    void handleSaveDraft();
  };

  // Initialise editor working copy on mount with an empty tree, and ensure
  // we land in edit mode.
  useEffect(() => {
    dispatch(setDocumentData([]));
    dispatch(setIsDirty(false));
    dispatch(setEditingAction(true));
  }, [dispatch]);

  const isSaving = createBook.isPending || saveDraft.isPending;

  return (
    <>
      {unsavedChangesModal}
      <PageContentLayout>
      <PageContentLayoutHeader
        buttons={
          <>
            <Button
              variant="alert"
              type="button"
              onClick={() => navigate({ to: '/documents' })}
            >
              Cancel <XIcon size={16} />
            </Button>
            <Button variant="info" type="button" onClick={handlePreview}>
              {isEditingAction ? 'Preview' : 'Edit'}
              {isEditingAction ? (
                <EyeIcon size={16} />
              ) : (
                <PencilIcon size={16} />
              )}
            </Button>
            <Button
              variant="info"
              type="button"
              onClick={() => {
                void handleSaveDraft();
              }}
              disabled={isSaving || !bookName.trim()}
            >
              {isSaving ? 'Saving…' : 'Save draft'}{' '}
              {isSaving ? (
                <Loader2Icon size={16} className="animate-spin" />
              ) : (
                <SaveIcon size={16} />
              )}
            </Button>
            <Button
              variant="default"
              type="button"
              onClick={handleSubmit}
              disabled={isSaving || !bookName.trim()}
            >
              Submit <ChevronRightIcon size={16} />
            </Button>
          </>
        }
      >
        <CustomTitle level="h1">
          {isEditingAction ? (
            <>
              Create:
              <TitleInputField
                placeholder="Book title..."
                value={bookName}
                onChange={e => setBookName(e.target.value)}
              />
            </>
          ) : (
            <>Preview: {bookName}</>
          )}
          <CustomTag>v1.0</CustomTag>
        </CustomTitle>
      </PageContentLayoutHeader>

      <PageContentLayoutSubheader>
        <CustomTag>Draft</CustomTag>
      </PageContentLayoutSubheader>

      <PageContentLayoutMain>
        {rootIds.map(id => (
          <DocumentEditorNode key={id} id={id} view={!isEditingAction} />
        ))}

        {isEditingAction && (
          <div className="document-node-view__add-buttons">
            <DocumentAddCtaButton label="Add part" onClick={handleAddPart} />
          </div>
        )}
      </PageContentLayoutMain>

      <ModalComponent
        isOpen={addNewBlockModal}
        setIsOpen={(bool: boolean) => dispatch(setNewBlockModal(bool))}
        body={<AddContentButton />}
        title="Add block"
        style={MODAL_ADD_BLOCK_STYLES}
      />

      <ModalComponent
        isOpen={specialUrlModal}
        setIsOpen={handleSpecialUrlModal}
        body={<ReferencerModal />}
        title="Select section"
        style={MODAL_REFERENCER_STYLES}
      />
    </PageContentLayout>
    </>
  );
}
